import * as React from 'react';
import { AxiosResponse } from 'axios';
import { useLocation } from 'react-router-dom';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { useNavigate } from 'react-router-dom';
import CalendarInput from 'common/calendar-input/calendar-input';
import CheckBox from 'common/checkbox/checkbox';
import DualInput from 'common/dual-input/dual-input';
import RadioButton from 'common/radio/radio';
import TextInput from 'common/text-input/text-input';
import TimeInput from 'common/time-input/time-input';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import './checklist-form.scss';

// Assets
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/icon_closecircle.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info_icon_1.svg';

// Data
import ChecklistFormData from '../checklist-form.json';
import DateSelector from 'common/date-selector/date-selector';
import DualInputSelector from 'common/dual-input-selector/dual-input-selector';
import { ICheckForm, ICheckItem, IChecklist, IWorkEmp } from 'types/checklist';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import { IOptionNumber } from 'types/common';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

const ChecklistForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [workEmpList, setWorkEmpList] = React.useState<IWorkEmp[]>([]);
  const [checkForm, setCheckForm] = React.useState<ICheckForm>({
    title: '', // 타이틀
    checklistItem: [], // 체크항목
    useMarkEmp: false,
    selectedMarkEmps: [],
    selectAllMarkEmp: false,
    checkTime: '',
    withPhoto: false,
  });

  const [responsibleStaffCheck, setResponsibleStaffCheck] = React.useState<boolean>(false);

  const [checklistItem, setChecklistItem] = React.useState<any>([]);
  const [dateLeaveTextbox, setDateLeaveTextbox] = React.useState<boolean>(false);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const fetchEmpList = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getEmps`);
      console.log('/auth/getEmps_data', data);

      if (data.resultCode === 0) {
        setWorkEmpList(data.resultData.emps);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getEmps');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 수정/삭제 시 불러온 데이터 상태 저장
  const fetchCheckItem = (data: IChecklist) => {
    // 체크항목 데이터 가공
    const checkItems: ICheckItem[] = [];
    data.LIST?.split('@@').map((i, _i) => {
      checkItems.push({ id: _i / 2, content: i });
    });
    // 담당직원 데이터 가공
    const markEmps: IOptionNumber[] = [];
    data.EMP_NAME2 !== '' &&
      data.EMP_NAME2?.split('@').map((i, _i) => {
        if (!!i && i.length > 0) {
          markEmps.push({ label: i, value: Number(data.EMP_SEQ?.split('@')[_i]) });
        }
      });

    setCheckForm({
      title: data.TITLE,
      checklistItem: checkItems,
      useMarkEmp: data.EMP_NAME2 ? true : false,
      selectedMarkEmps: markEmps,
      selectAllMarkEmp: false,
      checkTime: data.END_TIME,
      withPhoto: data.PHOTO_CHECK === '1',
    });
  };

  // 텍스트 인풋 상태 핸들러
  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    const { value } = e.target;
    setCheckForm((prev) => ({ ...prev, [name]: value }));
  };

  // 체크항목 추가
  const addCheckListItem = () => {
    let lastItem = { id: 0, content: '' };
    if (checkForm.checklistItem.length > 0) {
      lastItem = checkForm.checklistItem.reduce((pv, cv) => {
        return cv.id > pv.id ? cv : pv;
      });
    }
    setCheckForm((prev) => ({ ...prev, checklistItem: [...prev.checklistItem, { id: lastItem.id + 1, content: '' }] }));
  };

  // 체크항목 인풋 상태 핸들러
  const handleCheckItemInput = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setCheckForm((prev) => {
      const newCheckItmes = prev.checklistItem.map((i) => {
        if (i.id === id) {
          return { id: i.id, content: e.target.value };
        }
        return i;
      });
      return {
        ...prev,
        checklistItem: newCheckItmes,
      };
    });
  };

  // 체크 항목 삭제
  const removeCheckItem = (id: number) => {
    setCheckForm((prev) => ({ ...prev, checklistItem: prev.checklistItem.filter((i) => i.id !== id) }));
  };

  // 담당직원 셀렉트 상태 핸들러
  const handleMarkEmpSelect = (newValue: MultiValue<IOptionNumber>) => {
    const list = newValue.map((i) => ({ value: i.value, label: i.label }));
    setCheckForm((prev) => ({ ...prev, selectedMarkEmps: list }));
  };

  // 담당직원 전체 선택 토글
  const handleAllMarkEmpSelect = () => {
    setCheckForm((prev) => ({
      ...prev,
      selectAllMarkEmp: !prev.selectAllMarkEmp,
      selectedMarkEmps: prev.selectAllMarkEmp ? [] : markEmpOptions.map((i) => i),
    }));
  };

  // 체크예정시간 인풋 상태 핸들러
  const handleCheckTimeInput = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    const { value } = e.target;
    setCheckForm((prev) => ({ ...prev, [name]: value }));
  };

  // 담당직원, 사진등록 설정여부 토글
  const handleChecklistSetting = (name: string, value: string) => {
    setCheckForm((prev) => ({ ...prev, [name]: value === '1' ? true : false }));
  };

  // 체크리스트 추가 클릭 시 입력값 체크
  const validCheckForm = () => {
    if (checkForm.title === '') {
      return setModalState({ isModalShow: true, title: '체크리스트 등록', desc: '타이틀을 입력해 주세요' });
    } else if (checkForm.checklistItem.length === 0) {
      return setModalState({ isModalShow: true, title: '체크리스트 등록', desc: '체크항목을 한개 이상 추가해 주세요' });
    } else if (checkForm.checklistItem.find((i) => i.content === '')) {
      return setModalState({ isModalShow: true, title: '체크리스트 등록', desc: '입력되지 않은 체크항목이 있습니다' });
    } else {
      if (checkForm.useMarkEmp) {
        if (checkForm.selectedMarkEmps.length === 0) {
          return setModalState({
            isModalShow: true,
            title: '체크리스트 등록',
            desc: '담당직원이 설정되지 않았습니다. 담당직원 선택 또는 미사용 시에는 설정을 변경해 주세요',
          });
        }
      }
    }
    submitForm();
  };

  // 체크리스트 추가 요청
  const submitForm = async () => {
    const typeName = location.state?.checkItemData ? 'modify' : 'add';
    try {
      setLoadingState({ isFetchLoading: true });

      const list = checkForm.checklistItem.map(
        (i, _i) => `${_i === checkForm.checklistItem.length - 1 ? i.content : i.content + '@'}`,
      );
      const emps = checkForm.selectedMarkEmps.map((i) => ({ EMP_SEQ: i.value }));

      const { data }: AxiosResponse['data'] = await webApiNode(
        'post',
        `/auth/${typeName === 'add' ? 'checkRegister' : 'checkUpdate'}`,
        {
          CHECK_SEQ: location.state?.checkItemData.CHECK_SEQ,
          TITLE: checkForm.title,
          LIST: list,
          CHECK_EMP_FLAG: checkForm.useMarkEmp ? '1' : '0',
          EMP_SEQS: checkForm.useMarkEmp ? emps : [],
          END_TIME: checkForm.checkTime,
          PHOTO_CHECK: checkForm.withPhoto ? '1' : '0',
          CLOSE_FLAG: '0',
        },
      );
      console.log(`/auth/${typeName === 'add' ? 'checkRegister' : 'checkUpdate'}_data`, data);

      if (data.resultCode === 0) {
        navigate('/checklist');
        setModalState({
          isModalShow: true,
          title: '체크리스트 등록',
          desc: '완료되었습니다',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ` /auth/${typeName === 'add' ? 'checkRegister' : 'checkUpdate'}`);
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const selectArray = ['일', '목', '월', '화', '수', '금', '토'];

  const handleForm = (event: any) => {
    const name = event.target.name;
    const value = event.target.value;
    setCheckForm((values: any) => ({ ...values, [name]: value }));
  };

  const onCheckBoxChange = () => {
    console.log(!dateLeaveTextbox);
    setDateLeaveTextbox(!dateLeaveTextbox);
  };

  const recurringCycleDropdown = (data: any, name: string) => {
    setCheckForm((values: any) => ({ ...values, [name]: data }));
  };

  const handleResponsibleStaff = (event: any) => {
    setCheckForm((values: any) => ({ ...values, responsibleStaff: event.target.value }));
  };

  const handleDateChange = (date: any, option: string) => {
    if (option === 'START_DATE') {
      setCheckForm((value: any) => ({ ...value, startDate: date }));
    }
    if (option === 'END_DATE') {
      setCheckForm((value: any) => ({ ...value, endDate: date }));
    }
  };

  const handleSelects = (data: string) => {
    // if (formData.selects.toString().includes(data)) {
    //   const newSelectArray = formData.selects.filter((s: string) => s !== data);
    //   setCheckForm((values: any) => ({ ...values, selects: newSelectArray }));
    // } else {
    //   let newSelectArray = formData.selects;
    //   newSelectArray.push(data);
    //   setCheckForm((values: any) => ({ ...values, selects: newSelectArray }));
    // }
  };

  React.useEffect(() => {
    fetchEmpList();
    if (location.state?.checkItemData) {
      fetchCheckItem(location.state?.checkItemData);
    }
  }, []);

  const recurringCycleOptions = [
    { value: '1', label: '일별' },
    { value: '2', label: '주별' },
    { value: '3', label: '월별' },
  ];

  const recurringCycleOften = [
    { value: '1', label: '1주마다' },
    { value: '2', label: '2주마다' },
    { value: '3', label: '3주마다' },
  ];

  const recurringCycleArray = [
    { value: '1', label: '1주마다' },
    { value: '2', label: '2주마다' },
    { value: '3', label: '3주마다' },
  ];

  const colourStyles: StylesConfig<any, true> = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '56px',
    }),
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: '#D6E7E4',
        borderRadius: '50px',
        fontFamily: 'Spoqa Han Sans Neo',
        fontWeight: '400',
        fontSize: '14px',
        color: '#075453',
        height: '28px',
        padding: '2px 2px 2px 5px',
      };
    },
  };

  const dropdownStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      borderColor: '#E9E9E9',
      borderRadius: '8px',
      width: '100%',
      height: '56px',
      fontFamily: 'Spoqa Han Sans Neo',
      fontWeight: 400,
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  const markEmpOptions = workEmpList.map((i) => ({ value: i.EMP_SEQ, label: i.EMP_NAME }));

  const ref = React.useRef() as any;

  return (
    <PlainNavbar save={validCheckForm} backgroundColor="#f3f4f6" route="checklist" saveText="완료">
      <div className="checklist-form">
        <div className="checklist-form__head">
          <div className="checklist-form__head__head-text">체크리스트</div>
          <div className="checklist-form__head__subhead-text">체크리스트 정보를 입력해주세요</div>
        </div>

        <div className="checklist-form-cards">
          <div className="checklist-form__card-profile-changes">
            <table className="checklist-form__body__table">
              <tr>
                <td className="checklist-form__body__left">
                  <div className="checklist-form-basic-info">
                    <div className="checklist-form-basic-info__head">기본정보</div>
                    <div className="checklist-form-basic-info__body">
                      <div>
                        <TextInput
                          inputData={handleTextInput}
                          label="타이틀"
                          value={checkForm.title}
                          placeholder="타이틀 입력"
                          inputName="title"
                          maxLength={20}
                        />
                      </div>
                      <div className="checklist-items__body">
                        {checkForm.checklistItem.length > 0 &&
                          checkForm.checklistItem.map((i, index: number) => (
                            <div key={i.id}>
                              <div className="checklist-items-icon" onClick={() => removeCheckItem(i.id)}>
                                <CloseIcon className="checklist-items-icon__icon" />
                              </div>
                              <input
                                key={index}
                                type="text"
                                ref={ref}
                                placeholder="ex. 가스벨브 잠그기"
                                className={
                                  index !== activeIndex
                                    ? 'checklist-items__textbox-disabled'
                                    : 'checklist-items__textbox'
                                }
                                value={i.content}
                                onChange={(event) => handleCheckItemInput(event, i.id)}
                                onClick={() => {
                                  setActiveIndex(index);
                                }}
                                maxLength={30}
                              />
                            </div>
                          ))}
                        <div className="checklist-items__add-check-item">
                          <div className="checklist-items__add-check-item__text" onClick={addCheckListItem}>
                            +체크항목 추가
                          </div>
                        </div>
                      </div>

                      {/* <div className="checklist-form-basic-info__date">
                        <div className="checklist-form-basic-info__date__col1">
                          <DateSelector
                            onChange={(date: any) => handleDateChange(date, 'START_DATE')}
                            value={formData.startDate}
                            inputName="startDate"
                            height={56}
                            label="반복기간"
                            placeholder="종료일 선택"
                          />
                        </div>
                        <div className="checklist-form-basic-info__date__col2">
                          <DualInputSelector
                            defaultValue={formData.endDate}
                            inputData={(date: any) => handleDateChange(date, 'END_DATE')}
                            placeholder="종료일 선택"
                            height={56}
                            checkbox={{
                              text: '미정',
                              checkboxValue: dateLeaveTextbox,
                              onCheckBoxChange: onCheckBoxChange,
                            }}
                            disabled={dateLeaveTextbox}
                          />
                        </div>
                      </div>
                      <div className="checklist-form-basic-info__selections">
                        반복주기
                        <div className="checklist-form-basic-info__recurring-cycle">
                          <div className="checklist-form-basic-info__recurring-cycle__col1">
                            <Select
                              components={{
                                IndicatorSeparator: () => null,
                                DropdownIndicator: () => (
                                  <div style={{ paddingRight: '15px' }}>
                                    <ArrowDownIcon />
                                  </div>
                                ),
                              }}
                              placeholder="시급"
                              styles={dropdownStyles}
                              isSearchable={false}
                              options={recurringCycleOptions}
                              defaultValue={formData.cycleDropdown1}
                              value={formData.cycleDropdown1}
                              name="cycleDropdown1"
                              onChange={(event) => recurringCycleDropdown(event, 'cycleDropdown1')}
                            />
                          </div>
                          <div className="checklist-form-basic-info__recurring-cycle__col2">
                            <Select
                              components={{
                                IndicatorSeparator: () => null,
                                DropdownIndicator: () => (
                                  <div style={{ paddingRight: '15px' }}>
                                    <ArrowDownIcon />
                                  </div>
                                ),
                              }}
                              placeholder="시급"
                              styles={dropdownStyles}
                              isSearchable={false}
                              options={recurringCycleOften}
                              defaultValue={formData.cycleDropdown2}
                              value={formData.cycleDropdown2}
                              name="cycleDropdown2"
                              onChange={(event) => recurringCycleDropdown(event, 'cycleDropdown2')}
                            />
                          </div>
                          <div className="checklist-form-basic-info__recurring-cycle__col3">
                            {selectArray.map((data) => (
                              <div
                                onClick={() => handleSelects(data)}
                                className={
                                  formData.selects.toString().includes(data)
                                    ? 'checklist-form-basic-info__squares-center squares-selected'
                                    : 'checklist-form-basic-info__squares-center squares-not-selected'
                                }>
                                {data}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </td>
                <td className="checklist-form__body__right">
                  <div className="checklist-form-right__part1">
                    <div className="checklist-form-right__head">체크리스트란? </div>
                    <div className="checklist-form-right__body">
                      직원대상으로 반복해서 확인해야 하는 사항을 리스트로 체크할 수 있어요.
                    </div>
                  </div>
                  <div className="checklist-form-right__part1">
                    <div className="checklist-form-right__head">체크리스트 항목</div>
                    <div className="checklist-form-right__body">
                      체크항목을 입력해주세요. “체크항목 추가"를 눌러 리스트를 추가 할 수 있어요
                    </div>
                  </div>
                  {/* <div className="checklist-form-right__part1">
                    <div className="checklist-form-right__head">반복기간</div>
                    <div className="checklist-form-right__body">반복 종료일이 없다면,미정을 체크해주세요</div>
                  </div>
                  <div className="checklist-form-right__part1">
                    <div className="checklist-form-right__head">반복주기</div>
                    <div className="checklist-form-right__body">
                      반복기간 동안 어느 시점에 체크를 해야하는지 정해주세요
                    </div>
                  </div> */}
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div className="checklist-form-cards">
          <div className="checklist-form__card-profile-changes">
            <table className="checklist-form__body__table">
              <tr>
                <td className="checklist-form__body__left">
                  <div className="checklist-setting">
                    <div className="checklist-setting__head">설정</div>
                    <div className="checklist-setting__body">
                      <table className="checklist-setting__table">
                        <tr className="checklist-setting__table__tr">
                          <td>작업 결과를 담당직원만 체크해야 하나요?</td>
                          <td>
                            <RadioButton
                              checked={handleChecklistSetting}
                              text="네, 담당직원만"
                              value={'1'}
                              radioName="useMarkEmp"
                              isChecked={checkForm.useMarkEmp}
                            />
                          </td>
                          <td>
                            <RadioButton
                              checked={handleChecklistSetting}
                              text="아니오, 직원모두"
                              value={'0'}
                              radioName="useMarkEmp"
                              isChecked={!checkForm.useMarkEmp}
                            />
                          </td>
                        </tr>
                        <tr className="checklist-setting__table__tr">
                          <td>사진을 등록해야만 체크완료가 가능한가요?</td>
                          <td>
                            <RadioButton
                              checked={handleChecklistSetting}
                              text="네, 사진 필요"
                              value={'1'}
                              radioName="withPhoto"
                              isChecked={checkForm.withPhoto}
                            />
                          </td>
                          <td>
                            <RadioButton
                              checked={handleChecklistSetting}
                              text="아니오, 필요없음"
                              value={'0'}
                              radioName="withPhoto"
                              isChecked={!checkForm.withPhoto}
                            />
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </td>
                <td className="checklist-form__body__right">
                  <div className="checklist-form-right__part1">
                    <div className="checklist-form-right__head">체크리스트 설정</div>
                    <div className="checklist-form-right__body">
                      담당 직원을 설정하면 해당 직원만 알림이가고 체크하여 완료 처리할 수 있어요
                    </div>
                  </div>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div className="checklist-form-cards">
          <div className="checklist-form__card-profile-changes">
            <table className="checklist-form__body__table">
              <tr>
                <td className="checklist-form__body__left">
                  <div className="staff-in-charge">
                    <div className="staff-in-charge__body">
                      {checkForm.useMarkEmp && (
                        <div className="select-staff-dual-input">
                          <div className="select-staff-dual-input__text-label">담당 직원 선택</div>
                          <div className="select-staff-dual-input__inputs">
                            <div className="select-staff-dual-input__inputs__dropdown">
                              <div className="select-staff-dual-input__dropdown">
                                <Select
                                  className="select-staff-dual-input__dropdown-select"
                                  components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: () => (
                                      <div style={{ paddingRight: '15px' }}>
                                        <ArrowDownIcon />
                                      </div>
                                    ),
                                    SingleValue: () => (
                                      <div style={{ paddingRight: '15px' }}>
                                        <ArrowDownIcon />
                                      </div>
                                    ),
                                  }}
                                  isSearchable={false}
                                  styles={colourStyles}
                                  options={markEmpOptions}
                                  placeholder="직원선택"
                                  isMulti
                                  isClearable={false}
                                  value={checkForm.selectedMarkEmps}
                                  onChange={handleMarkEmpSelect}
                                  noOptionsMessage={() => <div>선택 가능한 직원이 없습니다</div>}
                                />
                              </div>
                            </div>
                            <div className="select-staff-dual-input__checkbox">
                              <div className="select-staff-dual-input__checkbox__div">
                                <CheckBox
                                  checked={handleAllMarkEmpSelect}
                                  defaultCheck={checkForm.selectAllMarkEmp}
                                  text={'직원 전체 선택'}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="select-staff-dual-input__time-input">
                        <div className="select-staff-dual-input__check-point">
                          <div className="select-staff-dual-input__text-label">체크 예정 시점</div>
                          <div className="select-staff-dual-input__info">
                            <div className="select-staff-dual-input__info__icon">
                              <InfoIcon />
                            </div>
                            설정한 시간에 완료 여부를 체크합니다
                          </div>
                        </div>
                        <TimeInput
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleCheckTimeInput(event, 'checkTime')
                          }
                          value={checkForm.checkTime}
                          inputName="whenToCheck"
                          iconSide="RIGHT"
                        />
                      </div>
                    </div>
                  </div>
                </td>
                <td className="checklist-form__body__right">
                  {checkForm.useMarkEmp && (
                    <div className="checklist-form-right__part1">
                      <div className="checklist-form-right__head">담당직원</div>
                      <div className="checklist-form-right__body">
                        체크리스트를 확인해야할 직원들을 선택해주세요. 전체적원 대상일 경우 ‘ 직원 전체 선택' 을
                        체크해주세요.
                      </div>
                    </div>
                  )}
                  <div className="checklist-form-right__part1">
                    <div className="checklist-form-right__head">체크예정시점</div>
                    <div className="checklist-form-right__body">설정한 시간에 직원들의 체크 여부를 확인합니다.</div>
                  </div>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </PlainNavbar>
  );
};
export default ChecklistForm;
